import { type GetTaskByIdQuery, TaskStatus, WorkflowStepCode } from '@exam-management/graphql';

import type { SubtaskDetails } from '../types';

function mapMetadata(data: GetTaskByIdQuery['wflGetTaskById']['task']['subtasks'][number] | null) {
  if (data !== null && data.metadata.__typename === 'TaskMetadataWithQSet') {
    return {
      qsetCode: data.metadata.qSet.code,
      qsetId: data.metadata.qSet.id,
      qConfigCode: data.metadata.qSet.qConfig.code?.toString() ?? '-',
      totalQuestions: data.metadata.qSet.qConfig.totalQuestions,
      timeLimits: data.metadata.qSet.qConfig.metadata.timeLimit?.values,
    };
  }

  return {
    qsetCode: '',
    qsetId: '',
    qConfigCode: '-',
    totalQuestions: 0,
    timeLimits: [],
  };
}

function mapSubtask(task: GetTaskByIdQuery['wflGetTaskById']['task']['subtasks'][number]) {
  const metadata = mapMetadata(task);

  return {
    id: task.id,
    code: task.code,
    dueDate: task.dueDate,
    status: task.status,
    assignee: {
      id: task.assignee.id,
      firstname: task.assignee.firstname,
      lastname: task.assignee.lastname,
    },
    currentWorkflowStep: {
      code: task.currentWorkflowStep.code,
      name: task.currentWorkflowStep.name,
    },
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    submittedAt: task.submittedAt,
    ...metadata,
  };
}

function mapSubtaskToTaskData(
  subtasks: GetTaskByIdQuery['wflGetTaskById']['task']['subtasks'],
): SubtaskDetails[] {
  return subtasks.map((subtask) => {
    const mapped = mapSubtask(subtask);

    return {
      ...mapped,
      timeLimits: mapped.timeLimits ?? [],
    };
  });
}

function mapAssignee(data: GetTaskByIdQuery['wflGetTaskById']['task'] | null) {
  return {
    firstname: data?.assignee.firstname ?? '',
    lastname: data?.assignee.lastname ?? '',
    id: data?.assignee.id ?? '',
  };
}

function mapOwner(data: GetTaskByIdQuery['wflGetTaskById']['task'] | null) {
  return {
    firstname: data?.owner.firstname ?? '',
    lastname: data?.owner.lastname ?? '',
    id: data?.ownerId ?? '',
  };
}

function mapWorkflowStep(data: GetTaskByIdQuery['wflGetTaskById']['task'] | null) {
  if (data === null) {
    return {
      code: WorkflowStepCode.ReviewQsetTodo,
      name: '',
    };
  }

  return {
    code: data?.currentWorkflowStep.code ?? WorkflowStepCode.ReviewQsetTodo,
    name: data?.currentWorkflowStep.name ?? '',
  };
}

function mapExamData(data: GetTaskByIdQuery['wflGetTaskById']['task'] | null) {
  if (data !== null && data.metadata.__typename === 'TaskMetadataWithExamCourse') {
    return {
      examId: data.metadata.examCourse?.examId?.toString() ?? '',
      examCode: data.metadata.examCourse?.code ?? '',
      examTitle: data.metadata.examCourse?.name ?? '',
    };
  }

  return {
    examId: '',
    examCode: '',
    examTitle: '',
  };
}

export default function mapTaskDataToTaskDetails(
  data?: GetTaskByIdQuery['wflGetTaskById']['task'],
) {
  if (!data) {
    return {
      id: '',
      name: '',
      code: '',
      dueDate: new Date(),
      status: TaskStatus.Todo,
      assignee: mapAssignee(null),
      assignerId: '',
      ownerId: '',
      currentWorkflowStep: mapWorkflowStep(null),
      subtasks: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      owner: mapOwner(null),
      ...mapExamData(null),
    };
  }

  return {
    id: data.id ?? '',
    name: data.name ?? '',
    code: data.code ?? '',
    dueDate: data.dueDate ?? new Date(),
    status: data.status ?? TaskStatus.Todo,
    assignee: mapAssignee(data),
    assignerId: data.assignerId ?? '',
    ownerId: data.ownerId ?? '',
    currentWorkflowStep: mapWorkflowStep(data),
    subtasks: mapSubtaskToTaskData(data.subtasks),
    createdAt: data.createdAt ?? new Date(),
    updatedAt: data.updatedAt ?? new Date(),
    owner: mapOwner(data),
    ...mapExamData(data),
  };
}
